// The client-side merchant model: how a foreign house's vessel reads on the
// board.
//
// A merchant vessel is a real `trade_ship` unit owned by `MERCHANT_PLAYER_ID`
// — an id no player holds — carrying the house it speaks for. Without this
// module it renders like any other ship in player 3's green (`playerColor`
// cycles the palette), which reads as "another player's fleet" rather than
// "a house from beyond the map".
//
// The house reaches the client on the unit node (`seaHouse`), so the client
// never has to guess: one hull, painted per house.
import {
  MERCHANT_HOUSE_COLORS,
  MERCHANT_SHIP_PREFAB_ID,
  UNIT_KIND_TO_PREFAB,
} from '@aod/ui/prefabs';

/**
 * At this standing the houses call less often and pay less. Mirrors
 * `STANDING_COLD` in `packages/sim/src/world/sea_standing.rs`.
 */
export const STANDING_COLD = -3;

/**
 * At this standing the houses stop calling altogether for a while. Mirrors
 * `STANDING_SHUNNED` in `packages/sim/src/world/sea_standing.rs`.
 */
export const STANDING_SHUNNED = -6;

/**
 * The owner id of a foreign merchant vessel. Mirrors `MERCHANT_PLAYER_ID` in
 * `packages/sim/src/components/trade_vessel.rs` — 251 is not a player and no
 * player holds it.
 */
export const MERCHANT_PLAYER_ID = 251;

/**
 * The house a unit speaks for, or null when it is not a merchant vessel.
 * A vessel is a merchant's when it belongs to the merchant player *and* the
 * sim says which house it is; a house number on its own is enough, but the
 * owner check keeps a stray prop from turning a player ship into a trader.
 */
export function merchantHouseOf(
  ownerId: number | undefined,
  house: number | undefined,
): number | null {
  if (ownerId !== MERCHANT_PLAYER_ID) return null;
  if (house === undefined || !Number.isInteger(house) || house < 0) return null;
  if (house >= MERCHANT_HOUSE_COLORS.length) return null;
  return house;
}

/** The prefab a merchant vessel renders as. */
export function merchantPrefabId(house: number): string {
  return MERCHANT_SHIP_PREFAB_ID;
}

/**
 * The colour the house paints its hull in — the merchant's faction colour,
 * overriding the player palette. Bone, old gold, cold steel: all outside the
 * eight player colours, so a house never reads as a player.
 */
export function merchantColour(house: number): string {
  return MERCHANT_HOUSE_COLORS[house] ?? MERCHANT_HOUSE_COLORS[0];
}

/**
 * The owner's standing with the houses, read off a city node. `null` when the
 * sea is not open at all (the sim only carries the prop then).
 */
export function seaStandingOf(props: Record<string, unknown> | undefined): number | null {
  const standing = props?.seaStanding;
  return typeof standing === 'number' ? standing : null;
}

/**
 * The prefab a unit renders as. A house's vessel gets the merchant hull;
 * everything else gets the prefab for its kind.
 */
export function unitPrefabId(
  kind: string,
  ownerId: number | undefined,
  house: number | undefined,
): string {
  const h = merchantHouseOf(ownerId, house);
  if (h !== null) return merchantPrefabId(h);
  return UNIT_KIND_TO_PREFAB[kind] ?? 'skeleton';
}
